import { motion } from "framer-motion";
import { ShieldAlert, ArrowRight, Phone, Rocket, FileText } from "lucide-react";

export function RegClosing({ settings }) {
  const phone = settings?.phone || "";
  const telHref = phone ? `tel:${phone.replace(/[^+\d]/g, "")}` : "/#contact";

  return (
    <>
      {/* Important Disclaimer */}
      <section data-testid="reg-disclaimer-section" className="relative py-14 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="rounded-2xl border border-[#FF8800]/30 bg-[#FF8800]/[0.04] p-7 sm:p-10"
            data-testid="reg-disclaimer"
          >
            <div className="flex items-center gap-3 mb-5">
              <ShieldAlert className="w-5 h-5 text-[#FF8800] shrink-0" />
              <p className="font-mono-tech text-xs tracking-[0.35em] text-[#FF8800] uppercase">
                Important Disclaimer
              </p>
            </div>
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
              LaunchLab provides business facilitation, documentation and coordination support.
              Registrations, licenses, approvals and certifications are granted only by the
              respective government authorities, statutory bodies or accredited certification
              organizations. Requirements vary depending on business activity, location, entity
              type and applicable laws. Approval is subject to the decision of the relevant
              authority. LaunchLab does not guarantee approval unless expressly agreed under
              applicable service terms.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="reg-closing" data-testid="reg-closing-section" className="relative py-20 sm:py-28 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,136,0,0.08),transparent_60%)] pointer-events-none" />

        {/* Document → ecosystem visual */}
        <div className="absolute inset-0 pointer-events-none hidden lg:flex items-center justify-end pr-24">
          <div className="relative">
            <div className="w-24 h-24 rounded-2xl glass flex items-center justify-center animate-float shadow-[0_0_50px_rgba(0,176,255,0.2)]">
              <FileText className="w-10 h-10 text-[#00B0FF]" strokeWidth={1.5} />
            </div>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="absolute inset-0 rounded-2xl border border-[#FF8800]/30 animate-pulse-node"
                style={{ transform: `scale(${1.5 + i * 0.6})`, animationDelay: `${i * 0.7}s`, opacity: 0.4 - i * 0.12 }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="max-w-3xl"
          >
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06]" data-testid="reg-closing-headline">
              <span className="text-gradient-hero">DON'T LET REGISTRATIONS</span>
              <br />
              <span className="text-gradient-hero">SLOW DOWN YOUR IDEA.</span>
            </h2>
            <p className="mt-6 font-display text-xl sm:text-2xl font-bold text-gradient-saffron">
              LET'S BUILD YOUR BUSINESS THE RIGHT WAY.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#smart-check"
                data-testid="reg-closing-cta-start"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-sm tracking-wide transition-transform duration-300 hover:scale-105 shadow-[0_0_40px_rgba(255,136,0,0.35)]"
              >
                START WITH LAUNCHLAB
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href={telHref}
                data-testid="reg-closing-cta-talk"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full glass text-slate-100 font-semibold text-sm tracking-wide transition-all duration-300 hover:border-[#00B0FF]/50 hover:shadow-[0_0_30px_rgba(0,176,255,0.2)]"
              >
                <Phone className="w-4 h-4 text-[#00E676]" />
                TALK TO OUR BUSINESS TEAM
              </a>
            </div>
            <div className="mt-16 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF8800] to-[#ff5e00] flex items-center justify-center">
                <Rocket className="w-4 h-4 text-[#06080E]" strokeWidth={2.4} />
              </div>
              <div>
                <p className="font-display font-extrabold tracking-[0.18em] text-white text-sm">
                  LAUNCH<span className="text-[#FF8800]">LAB</span>
                </p>
                <p className="font-mono-tech text-[9px] tracking-[0.3em] text-slate-500 uppercase">
                  Build. Launch. Transform.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
